// Greenhold SPA — Story 1.1 placeholder, restyled with @greenhold/tokens in
// Story 1.2, refactored to use @greenhold/components in Story 1.3.
// Story 1.15 — wraps in QueryClientProvider + smoke-mounts <BalanceDisplay />
// inside the existing card.
// Story 1.16 — adds <TopUpModal> behind the BalanceDisplay's onTopupClick.
// Story 1.17 — wires react-router-dom + adds /account/wallet route hosting
// the full-variant <BalanceDisplay>. (Story 8.3 removed the cashout and
// bank-account onboarding that used to live on that page with it.)
// Story 7.1 — retired the Story 1.3 smoke card that lived here as
// <HomePage />. `/` now renders <LandingPage /> (Live-A door) with
// embedded sign-in + create-account tabs. New routes: `/verify-email`
// + `/verify-email-pending`. See apps/web/src/features/auth/ for the
// SPA's first auth surface.
// App-shell — introduced the persistent role-aware navigation chrome
// (<AppShell />, apps/web/src/components/AppShell.tsx). The SPA used to
// mount every route bare, so a signed-in user could only move between
// surfaces by hand-typing URLs. Routes are now split into two groups:
//   * CHROMELESS — pages that own their full-screen layout (landing,
//     email-verification, the immersive /welcome reveal ceremony, and
//     the /prototype/* component sandboxes). No nav bar.
//   * SHELL — every authenticated surface, mounted under a single
//     <Route element={<AppShell />}> layout route that renders the top
//     bar + <Outlet />. Per-route AdminGuard / ApprovedCreatorGuard
//     wrappers are unchanged; they simply render inside the Outlet.
// Home stays /feed for all roles per the confirmed product vision.

import { Suspense, lazy } from "react";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";

import { AppShell } from "./components/AppShell";
import { BetaBanner } from "./components/BetaBanner";
import { AdminGuard } from "./components/AdminGuard";
import { AdminLayout } from "./components/AdminLayout";
import { ApprovedCreatorGuard } from "./components/ApprovedCreatorGuard";
import { AdminApplicationDetailPage } from "./pages/AdminApplicationDetailPage";
import { AdminFeedEditorialPage } from "./pages/AdminFeedEditorialPage";
import { AdminApplicationsPage } from "./pages/AdminApplicationsPage";
import { AdminUsersPage } from "./pages/AdminUsersPage";
import { AdminBulletinComposerPage } from "./pages/AdminBulletinComposerPage";
import { AdminBulletinListPage } from "./pages/AdminBulletinListPage";
import { AdminFinancialToolsPage } from "./pages/AdminFinancialToolsPage";
import { AdminLedgerExportPage } from "./pages/AdminLedgerExportPage";
import { AdminMetricsPage } from "./pages/AdminMetricsPage";
import { AdminPlatformClosePage } from "./pages/AdminPlatformClosePage";
import { AdminVatRegisterPage } from "./pages/AdminVatRegisterPage";
import { AdminModerationPage } from "./pages/AdminModerationPage";
import { AdminPortfolioApplicationDetailPage } from "./pages/AdminPortfolioApplicationDetailPage";
import { AdminPortfolioApplicationsPage } from "./pages/AdminPortfolioApplicationsPage";
import { AdminPortfolioComposerPage } from "./pages/AdminPortfolioComposerPage";
import { AdminPortfolioDetailPage } from "./pages/AdminPortfolioDetailPage";
import { AdminPortfoliosListPage } from "./pages/AdminPortfoliosListPage";
import { AdminDropsPage } from "./pages/AdminDropsPage";
import { BecomeCreatorPage } from "./pages/BecomeCreatorPage";
import { CreatorDashboardPage } from "./pages/CreatorDashboardPage";
import { CreatorPortfoliosPage } from "./pages/CreatorPortfoliosPage";
import { CreatorProfilePage } from "./pages/CreatorProfilePage";
import { CertificatePrototype } from "./prototype/CertificatePrototype";
import { CreatorEarningsTickerPrototype } from "./prototype/CreatorEarningsTickerPrototype";
import { DesignSystemPage } from "./prototype/DesignSystemPage";
import { DossierShopCardPrototype } from "./prototype/DossierShopCardPrototype";
import { OriginLinkFooterPrototype } from "./prototype/OriginLinkFooterPrototype";
import { RevealCeremonyPrototype } from "./prototype/RevealCeremonyPrototype";
import { SealedDossierPrototype } from "./prototype/SealedDossierPrototype";
import { TierBadgePrototype } from "./prototype/TierBadgePrototype";
import { DangerZonePage } from "./pages/DangerZonePage";
import { AccountPreferencesPage } from "./pages/AccountPreferencesPage";
import { AccountProfilePage } from "./pages/AccountProfilePage";
import { PublicCollectionPage } from "./pages/public/PublicCollectionPage";
import { PublicCertificatePage } from "./pages/public/PublicCertificatePage";
import { PublicSealedDossierPage } from "./pages/public/PublicSealedDossierPage";
import { GdprPage } from "./pages/GdprPage";
import { WalletPage } from "./pages/WalletPage";
import { RefundPage } from "./pages/RefundPage";
// Story 11.4 — one top-up's receipt, reached from Recent activity in the wallet
// and from the link on the top-up email.
import { ReceiptPage } from "./pages/ReceiptPage";
import { BuyDossierPage } from "./pages/BuyDossierPage";
import { ShareDetailPage } from "./pages/ShareDetailPage";
import { VaultPage } from "./pages/VaultPage";
// Lazy: the legal pages bundle three markdown documents as raw strings.
// They are read rarely and must not sit in the initial chunk — NFR-Perf-9
// caps SPA initial JS at 500KB gzip and we are close to it.
const LegalDocPage = lazy(() =>
  import("./pages/LegalDocPage").then((m) => ({ default: m.LegalDocPage })),
);
import { MarketplacePage } from "./pages/MarketplacePage";
import { ShopPage } from "./pages/ShopPage";
import { MyListingsPage } from "./pages/MyListingsPage";
import { WelcomePage } from "./pages/WelcomePage";
import { LandingPage } from "./pages/LandingPage";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";
import { VerifyEmailPendingPage } from "./pages/VerifyEmailPendingPage";
import { TwoFactorVerifyPage } from "./pages/TwoFactorVerifyPage";
import { FeedPage } from "./pages/FeedPage";
import { TosReacceptGate } from "./features/legal/TosReacceptGate";
import { isTosStaleError, notifyTosStale } from "./features/legal/tosGateStore";

// Story 7.25 — the user-terms staleness gate detects at the CACHE level, not
// per-hook: when the active terms move past a session's snapshot, the backend
// 426s every gated endpoint at once, and whichever query or mutation fails
// first must raise the gate. Individual hooks keep their own error handling
// untouched — this only observes.
const onTosStaleCheck = (error: unknown) => {
  if (isTosStaleError(error)) notifyTosStale();
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onTosStaleCheck }),
  mutationCache: new MutationCache({ onError: onTosStaleCheck }),
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Non-production builds only (renders null on REACT_APP_ENV=production).
          Above the router on purpose: chromeless routes (landing, reveal
          ceremony) must carry it too — the beta notice outranks immersion. */}
      <BetaBanner />
      <Toaster position="top-right" />
      <BrowserRouter>
        {/* Story 7.25 — blocking re-accept overlay for updated user terms.
            Inside the router (its Log out action navigates), above the
            Routes so it covers every surface, chromeless ones included:
            a stale session is stale everywhere. Lazy behind the gate flag —
            see TosReacceptGate for the bundle-size reasoning. */}
        <TosReacceptGate />
        <Routes>
          {/* ---- CHROMELESS routes — own their full-screen layout, no
               app-shell nav bar. ------------------------------------- */}

          <Route path="/" element={<LandingPage />} />
          {/* Story 7.1 — verify-email pending page shown after register
              submit and after a login attempt that hits EMAIL_NOT_VERIFIED. */}
          <Route
            path="/verify-email-pending"
            element={<VerifyEmailPendingPage />}
          />
          {/* Story 7.1 — verify-email landing page. Reads `?token=<>` from
              the URL (matches the backend-generated email link at
              server/src/services/auth_service.rs::verification_url). */}
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          {/* 2FA rework (2026-08-17) — login code step for TOTP-enrolled
              accounts. CHROMELESS: the visitor holds only a partial
              session, so no AppShell (its account-me fetch would 403). */}
          <Route path="/2fa/verify" element={<TwoFactorVerifyPage />} />
          {/* Migration 0047 — /welcome is retired; the verify-email 302
              still targets it, so this route forwards signed-in users to
              /shop (weekly free-claim card) and signed-out ones to the
              sign-in landing. */}
          <Route path="/welcome" element={<WelcomePage />} />

          {/* Story 10.6 — logged-out show-off surfaces. CHROMELESS + no
              guard: an anonymous viewer clicking a shared link must render
              without a session (AppShell's account-me fetch would 401).
              /u = public collection, /c = certificate permalink, /d =
              sealed-dossier permalink. */}
          <Route path="/u/:handle" element={<PublicCollectionPage />} />
          <Route path="/c/:shareId" element={<PublicCertificatePage />} />
          <Route path="/d/:dossierId" element={<PublicSealedDossierPage />} />

          {/* Prototype routes — component sandboxes per UX-DR Component
              Implementation Strategy (the team's MVP-Storybook
              substitute). No guard, no shell chrome. */}
          {/* Epic 8 — design-system gallery: tokens + component
              specimens + links to every /prototype/* sandbox. */}
          <Route path="/design-system" element={<DesignSystemPage />} />
          {/* Story 3.3 — Certificate hero-artifact visual review. */}
          <Route
            path="/prototype/certificate"
            element={<CertificatePrototype />}
          />
          <Route
            path="/prototype/creator-earnings-ticker"
            element={<CreatorEarningsTickerPrototype />}
          />
          {/* Story 2.7 — DossierShopCard visual review. No guard. */}
          <Route
            path="/prototype/dossier-shop-card"
            element={<DossierShopCardPrototype />}
          />
          {/* Story 3.6 — RevealCeremony orchestrator visual review.
              Bypasses useReveal (uses mock CertificateShare fixtures)
              so the ceremony plays without a logged-in session. */}
          <Route
            path="/prototype/reveal-ceremony"
            element={<RevealCeremonyPrototype />}
          />
          {/* Story 4.4 — OriginLinkFooter visual review. */}
          <Route
            path="/prototype/origin-link-footer"
            element={<OriginLinkFooterPrototype />}
          />
          {/* Story 3.4 — SealedDossier full 5-state machine visual review. */}
          <Route
            path="/prototype/sealed-dossier"
            element={<SealedDossierPrototype />}
          />
          {/* Story 3.5 — TierBadge canonical primitive visual review. */}
          <Route
            path="/prototype/tier-badge"
            element={<TierBadgePrototype />}
          />

          {/* ---- SHELL routes — every authenticated surface renders
               inside the persistent <AppShell /> top-bar via <Outlet />. */}
          <Route element={<AppShell />}>
            {/* Story 7.1 (stub) — post-login home. Real feed lands in
                Stories 7.4-7.6. */}
            <Route path="/feed" element={<FeedPage />} />

            {/* Story 4.2 — vault grid (EPIC-4 KEYSTONE READER). First SPA
                consumer of <SealedDossier size="thumb"> + <Certificate
                size="thumb"> in a real-data grid. Backend GET
                /api/v1/vault/items returns a polymorphic VaultItem[]
                (sealed | opened). Clicking sealed triggers useReveal inline
                (mirrors WelcomePage's overlay pattern). Clicking opened
                navigates to /vault/shares/:id (Story 4.3 fills in). */}
            <Route path="/vault" element={<VaultPage />} />
            {/* Story 4.3 — certificate-detail page (EPIC-4 DETAIL
                SURFACE). The collectible noun was renamed "share" →
                "certificate" (2026-08 legal decision D4), so the route is
                /vault/certificates/:id. */}
            <Route
              path="/vault/certificates/:share_id"
              element={<ShareDetailPage />}
            />
            {/* Back-compat: redirect the old /vault/shares/:id deep links
                (bookmarks, previously-shared URLs) to the renamed route. */}
            <Route
              path="/vault/shares/:share_id"
              element={<LegacyShareRedirect />}
            />

            {/* Story 6.10 / action 16 — the published legal documents.
                Unauthenticated on purpose: the creator application form's
                accept-checkbox links at /legal/creator-program, and terms
                you cannot read before signing in are not terms you can
                consent to. Rendered straight from docs/legal/*.md. */}
            <Route
              path="/legal/:slug"
              element={
                <Suspense
                  fallback={
                    <main className="mx-auto w-full max-w-3xl px-6 py-12 opacity-60">
                      Loading…
                    </main>
                  }
                >
                  <LegalDocRoute />
                </Suspense>
              }
            />

            {/* Story 5.4 — marketplace browse (EPIC-5 READER STORY).
                No auth guard — backend 401s; SPA shows sign-in prompt.
                The shell degrades to wordmark + Sign in for logged-out
                visitors so this surface still works. */}
            {/* In-app dossier shop — primary market (buy from the
                platform). Reuses the public GET /api/v1/shop/drops. No
                auth guard; the endpoint is public. */}
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/marketplace" element={<MarketplacePage />} />
            {/* Story 5.6 — seller's "My listings" management surface. */}
            <Route
              path="/account/marketplace/listings"
              element={<MyListingsPage />}
            />

            <Route path="/account/wallet" element={<WalletPage />} />
            <Route path="/account/wallet/refund" element={<RefundPage />} />
            {/* Story 11.4 — the path the WalletToppedUp email links to
                (`routes/webhooks.rs::RECEIPT_PATH_PREFIX`). Keep the two in
                step. */}
            <Route
              path="/account/wallet/receipts/:receiptId"
              element={<ReceiptPage />}
            />
            {/* Story 2.10 — deep-link target of the Astro public-site's
                DossierBuyButton. No auth guard; backend 401s if the user
                isn't logged in. */}
            <Route path="/account/buy/:dropId" element={<BuyDossierPage />} />
            <Route
              path="/account/danger-zone/delete"
              element={<DangerZonePage />}
            />
            <Route path="/account/gdpr" element={<GdprPage />} />
            {/* Story 3.7 — user-preferences hub (animation_preference radio
                group at v1; Story 3.8 will add skip-ceremony toggle). */}
            <Route
              path="/account/preferences"
              element={<AccountPreferencesPage />}
            />
            {/* Story 4.5 — public-profile hub (handle claim/change + bio +
                visibility toggle + view-link to the Astro public page). */}
            <Route path="/account/profile" element={<AccountProfilePage />} />
            <Route
              path="/account/become-creator"
              element={<BecomeCreatorPage />}
            />
            {/* Story 1.24 — creator browses currently-recruiting portfolios
                + applies. Approved-creator-only via <ApprovedCreatorGuard>. */}
            <Route
              path="/account/creator-portfolios"
              element={
                <ApprovedCreatorGuard>
                  <CreatorPortfoliosPage />
                </ApprovedCreatorGuard>
              }
            />
            {/* Story 1.26 — creator dashboard with Earnings + Applications
                + Dossiers. Aggregates via GET /api/v1/creators/me/dashboard. */}
            <Route
              path="/account/creator-dashboard"
              element={
                <ApprovedCreatorGuard>
                  <CreatorDashboardPage />
                </ApprovedCreatorGuard>
              }
            />
            <Route
              path="/account/creator-profile"
              element={<CreatorProfilePage />}
            />

            {/* Admin console. A single <AdminGuard> wraps the layout;
                <AdminLayout> renders the persistent sub-nav + <Outlet/> so
                every admin surface is reachable (previously only
                /admin/applications was linked). The backend's AdminUser
                extractor + 2FA middleware are the real authorization
                boundary — this guard is UX-only. Child paths are relative
                to /admin. */}
            <Route
              path="/admin"
              element={
                <AdminGuard>
                  <AdminLayout />
                </AdminGuard>
              }
            >
              {/* Default admin landing → Metrics (design review). Deep links
                  to every other admin surface keep working via their own
                  child routes below. */}
              <Route
                index
                element={<Navigate to="/admin/metrics" replace />}
              />
              {/* Admin User Management — account list + block/unblock. */}
              <Route path="users" element={<AdminUsersPage />} />
              {/* Story 1.27 — creator applications. */}
              <Route path="applications" element={<AdminApplicationsPage />} />
              <Route
                path="applications/:id"
                element={<AdminApplicationDetailPage />}
              />
              {/* Story 1.28 — portfolio-application review. */}
              <Route
                path="portfolio-applications"
                element={<AdminPortfolioApplicationsPage />}
              />
              <Route
                path="portfolio-applications/:id"
                element={<AdminPortfolioApplicationDetailPage />}
              />
              {/* Story 2.2 — portfolio composer. */}
              <Route path="portfolios" element={<AdminPortfoliosListPage />} />
              <Route
                path="portfolios/new"
                element={<AdminPortfolioComposerPage />}
              />
              <Route
                path="portfolios/:portfolio_id"
                element={<AdminPortfolioDetailPage />}
              />
              {/* Story 2.4 — drop-state cockpit. */}
              <Route path="drops" element={<AdminDropsPage />} />
              {/* Migration 0047 — the admin welcome-portfolio activation
                  surface is retired: the free dossier is now drawn from the
                  live shop drops (no admin-configured welcome portfolio). */}
              {/* Story 7.7 — editorial "Post to feed" composer. */}
              <Route
                path="feed/editorial"
                element={<AdminFeedEditorialPage />}
              />
              {/* Story 1.30 — Bulletin composer. */}
              <Route path="bulletin" element={<AdminBulletinListPage />} />
              <Route
                path="bulletin/new"
                element={<AdminBulletinComposerPage />}
              />
              <Route
                path="bulletin/:id"
                element={<AdminBulletinComposerPage />}
              />
              {/* Story 1.31 — moderation queue. */}
              <Route path="moderation" element={<AdminModerationPage />} />
              {/* Story 1.29 — metrics dashboard. */}
              <Route path="metrics" element={<AdminMetricsPage />} />
              {/* Story 1.32 — financial tools. */}
              <Route
                path="financial-tools"
                element={<AdminFinancialToolsPage />}
              />
              {/* Story 6.16 — per-user + platform-wide ledger export. */}
              <Route path="ledger-export" element={<AdminLedgerExportPage />} />
              {/* Story 6.17 — platform period close. */}
              <Route path="finance/close" element={<AdminPlatformClosePage />} />
              {/* Story 11.11 — the country VAT register: monitoring, and the
                  ONE audited, forward-only way a country rule is typed in. */}
              <Route path="vat-register" element={<AdminVatRegisterPage />} />
            </Route>
          </Route>

          {/* Catch-all — unknown paths (typos, and the Astro-only /shop +
              /shop/:dropId deep-links that don't exist in the SPA) bounce
              to the landing, which forwards signed-in users to /feed.
              Prevents blank no-route pages. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

/// Redirect the retired /vault/shares/:id path (the collectible was
/// renamed "share" → "certificate", 2026-08 legal D4) to the current
/// /vault/certificates/:id route, preserving the id. Keeps old bookmarks
/// + previously-shared links working.
function LegacyShareRedirect() {
  const { share_id } = useParams<{ share_id: string }>();
  return <Navigate to={`/vault/certificates/${share_id ?? ""}`} replace />;
}

/** Resolves /legal/:slug onto one of the three published documents.
 *  An unknown slug lands on the user terms rather than 404ing — a legal
 *  link that goes nowhere is the failure mode this route exists to fix. */
const LEGAL_SLUGS = ["terms", "creator-program", "privacy"] as const;
type LegalSlug = (typeof LEGAL_SLUGS)[number];

function LegalDocRoute() {
  const { slug } = useParams<{ slug: string }>();
  const resolved: LegalSlug = LEGAL_SLUGS.includes(slug as LegalSlug)
    ? (slug as LegalSlug)
    : "terms";
  return <LegalDocPage slug={resolved} />;
}
